import React, {useEffect, useMemo} from 'react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import PageLoader from '../../components/page-loader'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import Seo from '../../components/seo'
import Optimizely from '../../components/optimizely'
import {MAX_CACHE_AGE_CMS, STALE_WHILE_REVALIDATE_M} from '../../constants'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {hasOptimizelyContent} from '../../utils/optimizely-content'
import ContentFallback from '../../components/optimizely/utils/contentFallback'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useScript, setInitialScriptState} from '../../contexts/script-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {setCacheControl} from '../../utils/cache-control'
import {generateMetaTags, generateAlternateUrls} from '../../utils/utils'
import {useHomepageSeo} from '../../hooks/use-homepage-seo'

const Home = () => {
    const serverContext = useServerContext()
    const {res} = serverContext || {}
    // Use app origin for canonical URL (works in both SSR and CSR)
    const {enableScript, disableScript} = useScript()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_HOME', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }
    const IS_NOIBU_DISABLED_HOME = getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_HOME', siteId)

    const initialHomepageSeo = useMemo(() => {
        if (typeof window === 'undefined') {
            return serverContext?.req?.homepageSeoData || null
        }
        return window.__HOMEPAGE_SEO__ || null
    }, [serverContext])

    // Fetch homepage-specific SEO data directly (works with SSR)
    const {data: seoData} = useHomepageSeo(initialHomepageSeo)

    useEffect(() => {
        if (typeof window !== 'undefined' && window.__HOMEPAGE_SEO__) {
            try {
                delete window.__HOMEPAGE_SEO__
            } catch (error) {
                window.__HOMEPAGE_SEO__ = undefined
            }
        }
    }, [])

    const homepageSeo = seoData || initialHomepageSeo || {}

    // Was calling res.set() with a single argument, so the header name was missing entirely and the
    // homepage fell through to the platform default instead of the CMS cache policy.
    setCacheControl(res, {
        maxAge: MAX_CACHE_AGE_CMS,
        staleWhileRevalidate: STALE_WHILE_REVALIDATE_M
    })

    // Explicitly request 'homepage' content instead of relying on path mapping
    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('homepage')

    useEffect(() => {
        if (IS_NOIBU_DISABLED_HOME === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        return () => {
            if (IS_NOIBU_DISABLED_HOME) {
                enableScript('noibu')
            }
        }
    }, [])

    if (isOptimizelyLoading) {
        return (
            <Box data-testid="home-page" layerStyle="page" pb={0}>
                <Seo title={homepageSeo?.siteTitle} canonicalUrl={homepageSeo?.canonical}>
                    {<link rel="canonical" href={homepageSeo?.canonical} />}
                    {generateAlternateUrls(homepageSeo?.c_alternateURLs)}
                    {homepageSeo?.schemaMarkup && (
                        <script type="application/ld+json" fetchpriority="low">
                            {JSON.stringify(homepageSeo.schemaMarkup)}
                        </script>
                    )}
                    {generateMetaTags(homepageSeo?.pageGraphTagValues)}
                </Seo>
                <PageLoader />
            </Box>
        )
    }

    /* No authored homepage content for this (brand, locale): dev → diagnostic
     * banner, prod → standard error page. Centralized in ContentFallback. */
    const homepageContent = optimizelyData?.page || optimizelyData
    const hasContent = hasOptimizelyContent(homepageContent)
    if (!hasContent) {
        return (
            <Box data-testid="home-page" pb={0}>
                <Seo title={homepageSeo?.siteTitle} canonicalUrl={homepageSeo?.canonical}>
                    {<link rel="canonical" href={homepageSeo?.canonical} />}
                    {generateAlternateUrls(homepageSeo?.c_alternateURLs)}
                    {homepageSeo?.schemaMarkup && (
                        <script type="application/ld+json" fetchpriority="low">
                            {JSON.stringify(homepageSeo.schemaMarkup)}
                        </script>
                    )}
                    {generateMetaTags(homepageSeo?.pageGraphTagValues)}
                </Seo>
                <ContentFallback contentKey="homepage" />
            </Box>
        )
    }

    return (
        <Box data-testid="home-page" pb={0}>
            <Seo title={homepageSeo?.siteTitle} canonicalUrl={homepageSeo?.canonical}>
                {/* Performance: Preconnect to critical third-party origins */}
                <link rel="preconnect" href="https://cg.optimizely.com" crossOrigin="anonymous" />
                <link
                    rel="preconnect"
                    href="https://images.contentstack.io"
                    crossOrigin="anonymous"
                />
                <link rel="preconnect" href="https://edge.gdi.cloud" crossOrigin="anonymous" />
                <link rel="dns-prefetch" href="https://cg.optimizely.com" />
                <link rel="dns-prefetch" href="https://images.contentstack.io" />
                <link rel="dns-prefetch" href="https://edge.gdi.cloud" />
                {<link rel="canonical" href={homepageSeo?.canonical} />}
                {generateAlternateUrls(homepageSeo?.c_alternateURLs)}
                {homepageSeo?.schemaMarkup && (
                    <script type="application/ld+json" fetchpriority="low">
                        {JSON.stringify(homepageSeo.schemaMarkup)}
                    </script>
                )}
                {generateMetaTags(homepageSeo?.pageGraphTagValues)}
            </Seo>
            <Optimizely
                content={optimizelyData?.page || optimizelyData}
                isLoading={isOptimizelyLoading}
                disableSeo
            />
        </Box>
    )
}

Home.getTemplateName = () => 'home'

export default Home
